// assets/product-custom-property.js
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#updateConditionalVisibility();
    document.addEventListener('change', this.#handleDocumentChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('change', this.#handleDocumentChange);
  }

  #handleDocumentChange = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (!event.target.closest('product-custom-property-component')) return;
    this.#updateConditionalVisibility();
  };

  handleInput() {
    this.#updateCharacterCount();
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    if (!characterCount || !textInput) return;
    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }

  #updateConditionalVisibility() {
    const wrapper = this.querySelector('[data-custom-property-visible-when]');
    if (!(wrapper instanceof HTMLElement)) return;

    const expectedValue = (wrapper.dataset.customPropertyVisibleWhen || '').trim().toLowerCase();
    if (!expectedValue) return;

    const selectedValues = Array.from(document.querySelectorAll('product-custom-property-component input:checked'))
      .map((field) => {
        if (!(field instanceof HTMLInputElement)) return '';
        const component = field.closest('product-custom-property-component');
        return [field.value, field.name, component?.textContent || ''].join(' ');
      })
      .join(' ')
      .trim()
      .toLowerCase();

    const isVisible = selectedValues.includes(expectedValue);
    wrapper.hidden = !isVisible;

    for (const input of wrapper.querySelectorAll('input, textarea, select')) {
      input.disabled = !isVisible;
      if (!isVisible && input instanceof HTMLInputElement && input.type === 'file') {
        input.value = '';
      }
    }
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
